/**
 * map_tiles.ts — the World Atlas TILE PYRAMID, pure (no DOM, no workers).
 *
 * WHY (2026-09-21, "it takes a while to load in the map terrain or a filter
 * when you zoom in or out or move around"): the map used to sample ONE
 * 168² image of the current view and throw it away on every pan or zoom step
 * — and each pixel cost a terrain sample plus a five-sample forest test
 * (~10 s of CPU per view, sliced onto the game's own thread). Every slippy map
 * solves this the same way: fixed, world-aligned tiles at discrete zoom
 * levels, cached, and a coarser ancestor drawn stretched until the fine tile
 * arrives. Panning reuses what is drawn; zooming never blanks the map.
 *
 * Levels: level L's tile spans TILE_PX × BASE_MPP / 2^L metres — level 0 is a
 * 4.2 Mm tile, level 16 a 64 m tile (the map's max zoom is 6 px/m at 5 px a sample). The level
 * for a view is the one whose sample spacing is nearest `samplePx` SCREEN
 * pixels: the raster is deliberately coarser than the screen (a 4 px sample
 * on a 1000 px map is 250 samples across, not 1000), because the cost is
 * per SAMPLE and the picture is a tint, not a photograph.
 *
 * Tile coordinates are integers in the tile's own level; the origin is the
 * world origin. Keys are strings so a plain Map is the cache; the cache
 * stores whatever the DOM side hands it (an ImageBitmap in the browser, any
 * token in a node harness).
 */

export const TILE_PX = 64;
/** Metres per sample at level 0 (a level-0 tile is TILE_PX × this wide): 4.2 Mm — the whole 1.7 × 1.1 Mm continent
 *  fits in a handful of level-1 tiles. (At 8192 the whole-world view was 99 level-0 tiles, each finer than the screen.) */
export const BASE_MPP = 65536;
/** Level 16 is the deepest the map's 6 px/m zoom reaches at 5 px per sample; 17 leaves headroom for a finer sampler. */
export const MAX_LEVEL = 17;
/** How many ancestor levels a missing tile may borrow from (coarser = blurrier, but never blank). */
export const FALLBACK_DEPTH = 6;

export interface TileId { L: number; tx: number; tz: number }

/** Metres per sample at level L. */
export function levelMpp(L: number): number { return BASE_MPP / Math.pow(2, L); }
/** Metres a level-L tile spans. */
export function tileSpan(L: number): number { return TILE_PX * levelMpp(L); }

/** The level whose sample spacing is nearest `samplePx` screen pixels at `screenMpp` metres per screen pixel. */
export function levelFor(screenMpp: number, samplePx: number): number {
  const want = screenMpp * samplePx; // metres per sample we want
  const L = Math.round(Math.log2(BASE_MPP / want));
  return Math.max(0, Math.min(MAX_LEVEL, L));
}

/** The tile of level L under world (x, z). */
export function tileAt(L: number, x: number, z: number): TileId {
  const span = tileSpan(L);
  return { L, tx: Math.floor(x / span), tz: Math.floor(z / span) };
}

/** World rect of a tile. */
export function tileRect(t: TileId): { x0: number; z0: number; span: number } {
  const span = tileSpan(t.L);
  return { x0: t.tx * span, z0: t.tz * span, span };
}

export function tileKey(layer: string, t: TileId, bucket = 0): string {
  return bucket ? `${layer}:${t.L}:${t.tx}:${t.tz}:${bucket}` : `${layer}:${t.L}:${t.tx}:${t.tz}`;
}

/**
 * Tiles of level L covering a view centred on (cx, cz), W × H screen px at
 * `screenMpp`, plus `marginTiles` around it (pre-fetch), ordered CENTRE-OUT
 * so the request queue paints what the eye is on first.
 */
export function tilesInView(L: number, cx: number, cz: number, W: number, H: number, screenMpp: number, marginTiles = 0): TileId[] {
  const span = tileSpan(L);
  const hw = (W * screenMpp) / 2, hh = (H * screenMpp) / 2;
  const tx0 = Math.floor((cx - hw) / span) - marginTiles, tx1 = Math.floor((cx + hw) / span) + marginTiles;
  const tz0 = Math.floor((cz - hh) / span) - marginTiles, tz1 = Math.floor((cz + hh) / span) + marginTiles;
  const out: TileId[] = [];
  const ccx = cx / span - 0.5, ccz = cz / span - 0.5; // the view centre in tile units
  for (let tz = tz0; tz <= tz1; tz++) for (let tx = tx0; tx <= tx1; tx++) out.push({ L, tx, tz });
  out.sort((a, b) => ((a.tx - ccx) ** 2 + (a.tz - ccz) ** 2) - ((b.tx - ccx) ** 2 + (b.tz - ccz) ** 2));
  return out;
}

/** The ancestor `up` levels above `t` and the sub-rect (in [0,1] of the ancestor) that `t` occupies. */
export function ancestorOf(t: TileId, up: number): { tile: TileId; u0: number; v0: number; frac: number } {
  const k = Math.pow(2, up);
  const atx = Math.floor(t.tx / k), atz = Math.floor(t.tz / k);
  return { tile: { L: t.L - up, tx: atx, tz: atz }, u0: (t.tx - atx * k) / k, v0: (t.tz - atz * k) / k, frac: 1 / k };
}

/** A cached tile, or the nearest cached ancestor's sub-rect. */
export interface TileHit<T> { value: T; exact: boolean; u0: number; v0: number; frac: number; L: number }

/**
 * LRU cache of rendered tiles. `max` entries; a hit touches. The fallback
 * walk asks for the tile itself, then its parent, grandparent … up to
 * FALLBACK_DEPTH levels (a level-0 tile always exists once requested, so a
 * cold pan far away still shows the continent under the new detail).
 */
export class TileCache<T> {
  private readonly m = new Map<string, T>();
  constructor(readonly max = 512) {}
  get size(): number { return this.m.size; }
  has(key: string): boolean { return this.m.has(key); }
  get(key: string): T | undefined {
    const v = this.m.get(key);
    if (v !== undefined) { this.m.delete(key); this.m.set(key, v); } // LRU touch
    return v;
  }
  set(key: string, v: T, onEvict?: (v: T) => void): void {
    if (this.m.has(key)) this.m.delete(key);
    this.m.set(key, v);
    while (this.m.size > this.max) {
      const k = this.m.keys().next().value as string;
      const ev = this.m.get(k)!;
      this.m.delete(k);
      onEvict?.(ev);
    }
  }
  /** Drop every entry whose key starts with `prefix` (a layer's snapshot changed). */
  dropPrefix(prefix: string, onEvict?: (v: T) => void): number {
    let n = 0;
    for (const [k, v] of [...this.m]) if (k.startsWith(prefix)) { this.m.delete(k); onEvict?.(v); n++; }
    return n;
  }
  clear(onEvict?: (v: T) => void): void { if (onEvict) for (const v of this.m.values()) onEvict(v); this.m.clear(); }
  /** The tile or its nearest cached ancestor (null when none within `maxUp` levels — 0 = the exact tile only). */
  lookup(layer: string, t: TileId, bucket = 0, maxUp = FALLBACK_DEPTH): TileHit<T> | null {
    const own = this.get(tileKey(layer, t, bucket));
    if (own !== undefined) return { value: own, exact: true, u0: 0, v0: 0, frac: 1, L: t.L };
    for (let up = 1; up <= maxUp && t.L - up >= 0; up++) {
      const a = ancestorOf(t, up);
      const v = this.get(tileKey(layer, a.tile, bucket));
      if (v !== undefined) return { value: v, exact: false, u0: a.u0, v0: a.v0, frac: a.frac, L: a.tile.L };
    }
    return null;
  }
}

/**
 * The request plan for one frame: the tiles of the view's level that are not
 * cached, and — when the level is mostly cold — the coarse level two above it
 * first, so the first paint after a jump lands in a few tiles (a level-(L−2)
 * tile is 16× cheaper than the four… sixteen fine tiles it stands in for).
 */
export function planRequests<T>(
  cache: TileCache<T>, layer: string, bucket: number,
  L: number, cx: number, cz: number, W: number, H: number, screenMpp: number,
  coarseUp = 2, coldFraction = 0.5,
  /** A previous refresh of the same layer (last minute's sky, the last conflict generation) whose EXACT tiles
   *  stand in while the new ones fill: they count as warm, so no coarse level is asked for over them — the
   *  coarse tiles would land first and replace a sharp picture with a blurred one for a second or three. */
  warm?: { layer: string; bucket: number },
): { fine: TileId[]; coarse: TileId[]; visible: number; cachedExact: number } {
  const want = tilesInView(L, cx, cz, W, H, screenMpp, 0);
  const fine: TileId[] = [];
  let cachedExact = 0, warmExact = 0;
  for (const t of want) {
    if (cache.has(tileKey(layer, t, bucket))) { cachedExact++; continue; }
    fine.push(t);
    if (warm && cache.has(tileKey(warm.layer, t, warm.bucket))) warmExact++;
  }
  const coarse: TileId[] = [];
  const Lc = L - coarseUp;
  if (Lc >= 0 && want.length > 0 && (cachedExact + warmExact) / want.length < coldFraction) {
    for (const t of tilesInView(Lc, cx, cz, W, H, screenMpp, 0)) if (!cache.has(tileKey(layer, t, bucket))) coarse.push(t);
  }
  return { fine, coarse, visible: want.length, cachedExact };
}
